import React, { useCallback, useEffect, useState } from 'react';
import { be1Fetch } from '../../../../config/api';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../../../services/errorText';

export interface NodeDataTarget { nodeId: string; label: string; taskId: string; key?: string }

interface Props {
  dagId: string;
  /** 선택한 노드 */
  node: NodeDataTarget;
  /** 선행 노드들 — '입력' 탭에서 이들의 출력을 읽는다 */
  inputs: NodeDataTarget[];
  onClose: () => void;
}

interface Payload {
  dag_run_id?: string; task_id?: string; key?: string | null;
  columns: string[]; rows: any[]; total: number; limit: number;
  value?: any; message?: string;
}

const LIMITS = [100, 500, 1000];

/**
 * 노드 입력·출력 조회 — 선택한 노드가 넘긴 데이터(출력)와 선행 노드가 넘긴 데이터(입력)를 표로 본다.
 * 실행 기록(XCom·스테이징)에서 읽으므로 한 번은 실행되어 있어야 한다. 기본 100건.
 */
const NodeDataModal: React.FC<Props> = ({ dagId, node, inputs, onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const [tab, setTab] = useState<'input' | 'output'>('output');
  const [limit, setLimit] = useState(100);
  const [inputIdx, setInputIdx] = useState(0);
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const target = tab === 'output' ? node : inputs[inputIdx];

  const load = useCallback(() => {
    if (!target?.taskId) { setData(null); setError(t('property:nd.notTask')); return; }
    setLoading(true); setError(null);
    const q = `limit=${limit}${target.key ? `&key=${encodeURIComponent(target.key)}` : ''}`;
    be1Fetch(`/api/canvas/${dagId}/runs/latest/tasks/${encodeURIComponent(target.taskId)}/data?${q}`)
      .then(async (r) => { const b = await r.json(); if (!r.ok) throw new Error(errorText(b, t('property:nd.queryFailed'))); return b; })
      .then((d) => setData(d))
      .catch((e) => { setData(null); setError(e.message); })
      .finally(() => setLoading(false));
  }, [dagId, target, limit, t]);

  useEffect(() => { load(); }, [load]);

  const tabCls = (on: boolean) => `px-3 py-1 text-xs rounded ${on ? 'bg-blue-600 text-white' : 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700'}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-[980px] max-w-[95vw] h-[78vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('property:nd.title', { name: node.label })}</div>
            <div className="text-[0.6875rem] text-gray-500">{t('property:nd.subtitle')}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('property:nd.closeEsc')}>✕</button>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200">
          <button className={tabCls(tab === 'input')} onClick={() => setTab('input')}>{t('property:nd.input')} {inputs.length > 0 ? `(${inputs.length})` : ''}</button>
          <button className={tabCls(tab === 'output')} onClick={() => setTab('output')}>{t('property:nd.output')}</button>
          {tab === 'input' && inputs.length > 1 && (
            <select className="border border-gray-300 rounded px-2 py-1 text-xs bg-white" value={inputIdx} onChange={(e) => setInputIdx(Number(e.target.value))}>
              {inputs.map((i, k) => <option key={i.nodeId} value={k}>{i.label}</option>)}
            </select>
          )}
          <label className="ml-auto flex items-center gap-1 text-[0.6875rem] text-gray-600">{t('property:nd.limit')}
            <select className="border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
              {LIMITS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button className="px-2 py-0.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={load}>{t('property:nd.reload')}</button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto p-3">
          {tab === 'input' && inputs.length === 0 ? (
            <div className="text-xs text-gray-500">{t('property:nd.noUpstream')}</div>
          ) : loading ? <div className="text-xs text-gray-500">{t('property:nd.loading')}</div>
            : error ? <div className="text-xs text-red-500">{error}</div>
              : !data ? null
                : data.rows.length > 0 ? (
                  <>
                    <div className="text-[0.6875rem] text-gray-500 mb-1">
                      {t('property:nd.shown', { name: target.label, n: data.rows.length, total: data.total })}{data.total > data.rows.length ? t('property:nd.topOnly', { n: data.limit }) : ''})
                    </div>
                    <div className="overflow-auto border border-gray-200 rounded">
                      <table className="text-xs w-full">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-right text-gray-400 font-normal border-b border-gray-200">#</th>
                            {data.columns.map((c) => <th key={c} className="px-2 py-1 text-left font-medium text-gray-700 border-b border-gray-200 whitespace-nowrap">{c}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {data.rows.map((r, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-2 py-0.5 text-right text-gray-300 border-b border-gray-100 select-none">{i + 1}</td>
                              {data.columns.map((c) => (
                                <td key={c} className="px-2 py-0.5 border-b border-gray-100 whitespace-nowrap max-w-[280px] truncate" title={String(r[c] ?? '')}>
                                  {r[c] === null || r[c] === undefined ? <span className="text-gray-300">(null)</span> : String(r[c])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : data.value !== undefined ? (
                  <>
                    <div className="text-[0.6875rem] text-gray-500 mb-1">{t('property:nd.notRows', { name: target.label })}</div>
                    <pre className="text-[0.6875rem] font-mono bg-gray-50 border border-gray-200 rounded p-2 overflow-auto">{JSON.stringify(data.value, null, 2)}</pre>
                  </>
                ) : (
                  <div className="text-xs text-gray-500">{data.message || t('property:nd.noData')}</div>
                )}
        </div>

        <div className="px-4 py-2.5 border-t border-gray-200 flex items-center gap-2">
          <span className="text-[0.6875rem] text-gray-500 mr-auto">
            {data?.dag_run_id ? t('property:nd.run', { id: data.dag_run_id }) : ''} {data?.key ? `· XCom ${data.key}` : ''}
          </span>
          <button className="px-3 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={onClose}>{t('property:nd.close')}</button>
        </div>
      </div>
    </div>
  );
};

export default NodeDataModal;
